import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import KeyboardBackspaceOutlinedIcon from "@mui/icons-material/KeyboardBackspaceOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ApiConstants } from "../../main/constants/ApiConstants";
import useGetUser from "../../main/hooks/useGetUser";
import "../../main/theme/theme.css";
import { AppTheme, getStoredMainLandingTheme, MAIN_LANDING_THEMES } from "../../main/theme/publicTheme";
import { invalidateUser } from "../../main/store/stores/user/user.store";
import PublicLanguageSwitch from "../hotel/public/PublicLanguageSwitch";
import "../hotel/public/PublicExperience.css";

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const THEME_LABELS: Record<AppTheme, string> = {
  light: "Light",
  sky: "Sky",
  dark: "Dark",
  ocean: "Ocean",
  pearl: "Pearl",
  forest: "Forest",
};

const PublicUserActivation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useGetUser();
  const [theme, setTheme] = useState<AppTheme>(getStoredMainLandingTheme);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const themeDropdownRef = useRef<HTMLDivElement | null>(null);
  const [searchParams] = useSearchParams();
  const initialEmail = ((currentUser as any)?.email ?? searchParams.get("email") ?? "").trim().toLowerCase();
  const [activationEmail, setActivationEmail] = useState(initialEmail);
  const [activationCode, setActivationCode] = useState("");
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationMessage, setActivationMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isThemeOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!themeDropdownRef.current?.contains(event.target as Node)) {
        setIsThemeOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsThemeOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isThemeOpen]);

  const canSubmit = useMemo(
    () => activationEmail.trim().length > 0 && activationCode.trim().length === 6,
    [activationCode, activationEmail]
  );

  const handleActivationVerify = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!activationEmail.trim() || !activationCode.trim()) {
      setError("Email and OTP are required.");
      return;
    }

    try {
      setActivationLoading(true);
      setError("");
      setActivationMessage("");

      const response = await publicApi.post(ApiConstants.publicUser.verifyActivation, {
        email: activationEmail.trim().toLowerCase(),
        otpCode: activationCode.trim(),
      });

      const nextUser = {
        id: Number(response?.data?.id ?? response?.data?.Id ?? 0),
        email: response?.data?.email ?? response?.data?.Email ?? activationEmail.trim().toLowerCase(),
        userName: response?.data?.userName ?? response?.data?.UserName ?? "",
        firstName: response?.data?.firstName ?? response?.data?.FirstName ?? "",
        lastName: response?.data?.lastName ?? response?.data?.LastName ?? "",
        phone: response?.data?.phone ?? response?.data?.Phone ?? "",
        avatar: response?.data?.avatar ?? response?.data?.Avatar ?? "",
      };

      dispatch(invalidateUser());
      setActivationCode("");
      setActivationMessage("Account activated successfully. Redirecting...");

      window.setTimeout(() => {
        navigate(`/login${nextUser.email ? `?email=${encodeURIComponent(nextUser.email)}` : ""}`);
      }, 700);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data || "Activation failed.");
    } finally {
      setActivationLoading(false);
    }
  };

  const handleActivationResend = async () => {
    if (!activationEmail.trim()) {
      setError(t("generic.email"));
      return;
    }

    try {
      setActivationLoading(true);
      setError("");
      setActivationMessage("");

      const response = await publicApi.post(ApiConstants.publicUser.resendActivation, {
        email: activationEmail.trim().toLowerCase(),
      });

      setActivationMessage(response?.data?.message ?? "Activation code sent successfully.");
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data || "Unable to resend activation code.");
    } finally {
      setActivationLoading(false);
    }
  };

  return (
    <div className={`public-activation-page bcShell bcTheme-${theme}`}>
      <header className="or-hotels-top-nav public-activation-nav">
        <div className="or-hotels-nav-container">
          <div className="or-hotels-nav-left">
            <button type="button" className="or-hotels-brand" onClick={() => navigate("/booking")}>
              <span className="or-hotels-brand-icon">
                <ApartmentOutlinedIcon fontSize="inherit" />
              </span>
              <span className="or-hotels-brand-text">{t("publicBooking.activationTitle", { defaultValue: "Activate your account" })}</span>
            </button>
          </div>

          <div className="or-hotels-nav-right">
            <button type="button" className="or-hotels-monitor-btn" onClick={() => navigate("/booking")}>
              <KeyboardBackspaceOutlinedIcon fontSize="inherit" />
              {t("generic.back")}
            </button>

            <div className="theme-dropdown or-hotels-theme-dropdown" ref={themeDropdownRef}>
              <button
                type="button"
                className="theme-dropdown-trigger or-hotels-theme-trigger"
                onClick={() => setIsThemeOpen((current) => !current)}
                aria-haspopup="menu"
                aria-expanded={isThemeOpen}
                aria-label={t("generic.theme", { defaultValue: "Theme" })}
              >
                <PaletteOutlinedIcon fontSize="inherit" />
                <span>{THEME_LABELS[theme]}</span>
                <KeyboardArrowDownRoundedIcon fontSize="inherit" />
              </button>

              {isThemeOpen ? (
                <div className="theme-dropdown-menu or-hotels-theme-menu" role="menu">
                  {MAIN_LANDING_THEMES.map((themeOption) => (
                    <button
                      key={themeOption}
                      type="button"
                      className={`theme-dropdown-item ${theme === themeOption ? "active" : ""}`}
                      onClick={() => {
                        setTheme(themeOption);
                        setIsThemeOpen(false);
                      }}
                      role="menuitemradio"
                      aria-checked={theme === themeOption}
                    >
                      <span className={`theme-dot theme-dot--${themeOption}`} />
                      <span>{THEME_LABELS[themeOption]}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <PublicLanguageSwitch />
          </div>
        </div>
      </header>

      <section className="public-activation-shell">
        <div className="public-activation-topline">
          <span className="public-activation-kicker">
            <KeyOutlinedIcon fontSize="inherit" />
            {t("publicBooking.activationKicker", { defaultValue: "Account activation" })}
          </span>
        </div>

        <div className="public-activation-grid">
          <div className="public-activation-panel public-activation-copy">
            <h1>{t("publicBooking.activationTitle", { defaultValue: "Activate your account" })}</h1>
            <p>
              {t("publicBooking.activationIntro", {
                defaultValue: "Enter the 6-digit OTP sent to your email. If the code expired, request a new one and continue with the same public booking flow."
              })}
            </p>
          </div>

          <div className="public-activation-panel">
            <div className="public-activation-side-list">
              <div className="public-activation-side-item">
                <strong>{t("publicBooking.activationStepOne", { defaultValue: "Step 1" })}</strong>
                <span>{t("publicBooking.activationStepOneCopy", { defaultValue: "Use the same email address you registered with." })}</span>
              </div>
              <div className="public-activation-side-item">
                <strong>{t("publicBooking.activationStepTwo", { defaultValue: "Step 2" })}</strong>
                <span>{t("publicBooking.activationStepTwoCopy", { defaultValue: "Paste the latest OTP from your email inbox." })}</span>
              </div>
              <div className="public-activation-side-item">
                <strong>{t("publicBooking.activationStepThree", { defaultValue: "Step 3" })}</strong>
                <span>{t("publicBooking.activationStepThreeCopy", { defaultValue: "After activation, continue to login and send your booking request." })}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="public-activation-panel" style={{ marginTop: 22 }}>
          <form className="public-activation-form" onSubmit={handleActivationVerify}>
            <div className="public-activation-field">
              <label htmlFor="activate-email">{t("generic.email")}</label>
              <input
                id="activate-email"
                type="email"
                value={activationEmail}
                onChange={(e) => setActivationEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>

            <div className="public-activation-field">
              <label htmlFor="activate-otp">OTP</label>
              <input
                id="activate-otp"
                type="text"
                maxLength={6}
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
              />
            </div>

            {error ? <div className="public-activation-error">{error}</div> : null}
            {activationMessage ? <div className="public-activation-message">{activationMessage}</div> : null}

            <div className="public-activation-actions">
              <button type="submit" className="public-activation-primary" disabled={activationLoading || !canSubmit}>
                {activationLoading ? t("generic.saving") : t("publicBooking.activateNow", { defaultValue: "Activate account" })}
              </button>
              <button type="button" className="public-activation-secondary" onClick={handleActivationResend} disabled={activationLoading}>
                {t("publicBooking.resendOtp", { defaultValue: "Resend OTP" })}
              </button>
              <button
                type="button"
                className="public-activation-secondary"
                onClick={() => navigate(`/login${activationEmail ? `?email=${encodeURIComponent(activationEmail)}` : ""}`)}
              >
                {t("publicBooking.backToLogin", { defaultValue: "Back to login" })}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default PublicUserActivation;
